import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useIssuerEntitiesQuery } from '@/modules/issuer-dashboard/queries/entities.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { SearchFieldWithOptions, SelectOption } from 'ui';
import { EntityStatus } from 'api';

type entityOptionValue = {
  entity_id: string;
  id: number;
};

const renderSearchFieldOptionLabel: (option: SelectOption<entityOptionValue>) => ReactNode = ({
  label,
  value,
}) => (
  <div className="w-full flex justify-between text-sm">
    <span>{label}</span> <span className="text-neutral-500">{value.entity_id}</span>
  </div>
);

const HolderSearch = () => {
  const [params] = useSearchParams();
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentRegisterId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');

  const {
    isFetching: isEntitiesFetching,
    data: searchEntitiesData,
    fetchNextPage: fetchNextEntitiesPage,
    hasNextPage: hasNextEntitiesPage,
  } = useIssuerEntitiesQuery({
    search,
    page_size: 15,
    status: EntityStatus.EntityApproved,
    register_ids: [currentRegisterId].toString(),
  });

  const entitiesOptions = useMemo(
    () =>
      (searchEntitiesData?.pages || []).reduce((acc, { data }) => {
        return [
          ...acc,
          ...data.map(({ entity_name, id, entity_id }) => ({
            label: entity_name,
            value: { id, entity_id },
          })),
        ];
      }, [] as SelectOption<entityOptionValue>[]),
    [searchEntitiesData],
  );

  const handleSelectEntity = useCallback(
    (value: entityOptionValue) => {
      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(value.id.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  return (
    <SearchFieldWithOptions<entityOptionValue>
      options={entitiesOptions}
      onSelect={handleSelectEntity}
      hasMore={hasNextEntitiesPage}
      isLoading={isEntitiesFetching}
      fetchNextPage={fetchNextEntitiesPage}
      onSearch={setSearch}
      renderLabel={renderSearchFieldOptionLabel}
      placeholder="HIN/SRN or Holder Name"
    />
  );
};

export default HolderSearch;
